import { useState } from "react";

const hoy = new Date();
const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

function formatearFechaLarga(fechaISO) {
  if (!fechaISO) return "____________";
  const [anio, mes, dia] = fechaISO.split("-").map(Number);
  return `${dia} de ${MESES[mes - 1]} de ${anio}`;
}

function formatearSoles(valor) {
  const n = Number(valor);
  if (!valor || isNaN(n)) return "";
  return n.toLocaleString("es-PE", {
    style: "currency",
    currency: "PEN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function escaparHTML(texto) {
  const div = document.createElement("div");
  div.textContent = texto ?? "";
  return div.innerHTML;
}

export default function ConstanciaDeTrabajo() {
  const [empresa, setEmpresa] = useState("");
  const [ruc, setRuc] = useState("");
  const [direccion, setDireccion] = useState("");
  const [representante, setRepresentante] = useState("");
  const [cargoRepresentante, setCargoRepresentante] = useState("");

  const [trabajador, setTrabajador] = useState("");
  const [tipoDocumento, setTipoDocumento] = useState("DNI");
  const [numeroDocumento, setNumeroDocumento] = useState("");
  const [cargo, setCargo] = useState("");
  const [area, setArea] = useState("");

  const [fechaIngreso, setFechaIngreso] = useState("");
  const [fechaCese, setFechaCese] = useState("");
  const [fechaEmision, setFechaEmision] = useState(hoy.toISOString().slice(0, 10));
  const [ciudad, setCiudad] = useState("Lima");

  const [incluirRemuneracion, setIncluirRemuneracion] = useState(false);
  const [remuneracion, setRemuneracion] = useState("");
  const [incluirHorario, setIncluirHorario] = useState(false);
  const [horario, setHorario] = useState("");

  const documentoIniciado = empresa || trabajador || cargo || fechaIngreso || representante;
  const sigueLaborando = !fechaCese;

  const inputClase =
    "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700";
  const labelClase = "block text-sm font-medium text-slate-700 mb-1.5";
  const requerido = <span className="text-red-700">*</span>;
  const seccionClase = "mb-3 text-xs font-semibold tracking-wide uppercase text-slate-400";

  const textoConstancia = (
    <>
      <div className="mb-8 text-center">
        <h2 className="text-lg font-bold uppercase">{empresa || "[RAZÓN SOCIAL]"}</h2>
        {ruc && <p className="text-xs text-slate-500">RUC N.° {ruc}</p>}
        {direccion && <p className="text-xs text-slate-500">{direccion}</p>}
      </div>

      <hr className="mb-8" />

      <div className="mb-10 text-center">
        <p className="text-xl font-bold tracking-[0.25em] uppercase">Constancia de Trabajo</p>
      </div>

      <p className="mb-6 text-justify">
        Por medio de la presente, la empresa <strong>{empresa || "[Razón Social]"}</strong>
        {ruc && (
          <>
            , identificada con RUC N.° <strong>{ruc}</strong>
          </>
        )}
        {direccion && (
          <>
            , con domicilio fiscal en <strong>{direccion}</strong>
          </>
        )}
        , debidamente representada por <strong>{representante || "[Representante Legal]"}</strong>
        {cargoRepresentante && (
          <>
            , en su calidad de <strong>{cargoRepresentante}</strong>
          </>
        )}
        , deja constancia de que:
      </p>

      <p className="mb-6 text-justify indent-10">
        El(La) señor(a) <strong>{trabajador || "[Nombres y apellidos]"}</strong>,
        identificado(a) con {tipoDocumento} N.° <strong>{numeroDocumento || "____________"}</strong>,
        {sigueLaborando ? " mantiene vínculo laboral vigente" : " mantuvo vínculo laboral"} con
        nuestra organización, desempeñándose en el cargo de <strong>{cargo || "[Cargo del trabajador]"}</strong>
        {area && (
          <>
            {" "}
            en el área de <strong>{area}</strong>
          </>
        )}
        , desde el <strong>{formatearFechaLarga(fechaIngreso)}</strong>
        {sigueLaborando ? (
          <>
            {" "}
            hasta la fecha, cumpliendo sus funciones de manera regular y de acuerdo con las
            responsabilidades inherentes a su puesto.
          </>
        ) : (
          <>
            {" "}
            hasta el <strong>{formatearFechaLarga(fechaCese)}</strong>.
          </>
        )}
      </p>

      {incluirRemuneracion && remuneracion && (
        <p className="mb-6 text-justify indent-10">
          Asimismo, se deja constancia de que percibe (o percibió) una remuneración mensual de{" "}
          <strong>{formatearSoles(remuneracion)}</strong>.
        </p>
      )}

      {incluirHorario && horario && (
        <p className="mb-6 text-justify indent-10">
          Su jornada ordinaria de trabajo corresponde a <strong>{horario}</strong>.
        </p>
      )}

      <p className="mb-6 text-justify indent-10">
        La presente constancia se expide a solicitud del interesado(a), para los fines que estime
        convenientes, sin que ello implique reconocimiento de derechos distintos a los derivados de
        la relación laboral mantenida entre las partes.
      </p>

      <p className="mt-12 mb-20 text-right">
        <strong>{ciudad || "____________"}</strong>, {formatearFechaLarga(fechaEmision)}.
      </p>

      <div className="mt-24 text-center">
        <div className="mx-auto mb-3 border-t w-72 border-slate-400"></div>
        <p className="mt-3 font-semibold">{representante || "[Representante Legal]"}</p>
        <p className="text-sm">{cargoRepresentante || "[Cargo del representante]"}</p>
        <p className="mt-1 text-sm font-medium">{empresa || "[Razón Social]"}</p>
        {ruc && <p className="text-xs text-slate-500">RUC N.° {ruc}</p>}
      </div>
    </>
  );

  function imprimirConstancia() {
    const remuneracionTexto = incluirRemuneracion && remuneracion ? formatearSoles(remuneracion) : "";
    const horarioTexto = incluirHorario && horario ? escaparHTML(horario) : "";

    const html = `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Constancia de Trabajo</title>
<style>
  @page { margin: 2.5cm; }
  body { font-family: Georgia, 'Times New Roman', serif; color: #1e293b; line-height: 1.6; max-width: 720px; margin: 0 auto; padding: 40px 20px; }
  h2 { text-align: center; text-transform: uppercase; font-size: 18px; margin-bottom: 4px; }
  .meta { text-align: center; font-size: 12px; color: #64748b; margin: 0; }
  hr { border: none; border-top: 1px solid #cbd5e1; margin: 24px 0; }
  .titulo { text-align: center; font-size: 20px; font-weight: bold; letter-spacing: 0.25em; text-transform: uppercase; margin: 32px 0; }
  p { text-align: justify; margin-bottom: 20px; }
  .indent { text-indent: 2.5em; }
  .fecha { text-align: right; margin-top: 48px; }
  .firma { text-align: center; margin-top: 96px; }
  .linea { border-top: 1px solid #94a3b8; width: 280px; margin: 0 auto 12px; }
  .firma p { text-align: center; margin: 2px 0; }
</style>
</head>
<body>
  <h2>${escaparHTML(empresa || "[RAZÓN SOCIAL]")}</h2>
  ${ruc ? `<p class="meta">RUC N.° ${escaparHTML(ruc)}</p>` : ""}
  ${direccion ? `<p class="meta">${escaparHTML(direccion)}</p>` : ""}
  <hr />
  <p class="titulo">Constancia de Trabajo</p>
  <p>
    Por medio de la presente, la empresa <strong>${escaparHTML(empresa || "[Razón Social]")}</strong>
    ${ruc ? `, identificada con RUC N.° <strong>${escaparHTML(ruc)}</strong>` : ""}
    ${direccion ? `, con domicilio fiscal en <strong>${escaparHTML(direccion)}</strong>` : ""}
    , debidamente representada por <strong>${escaparHTML(representante || "[Representante Legal]")}</strong>
    ${cargoRepresentante ? `, en su calidad de <strong>${escaparHTML(cargoRepresentante)}</strong>` : ""}
    , deja constancia de que:
  </p>
  <p class="indent">
    El(La) señor(a) <strong>${escaparHTML(trabajador || "[Nombres y apellidos]")}</strong>,
    identificado(a) con ${escaparHTML(tipoDocumento)} N.° <strong>${escaparHTML(numeroDocumento || "____________")}</strong>,
    ${sigueLaborando ? "mantiene vínculo laboral vigente" : "mantuvo vínculo laboral"} con nuestra
    organización, desempeñándose en el cargo de <strong>${escaparHTML(cargo || "[Cargo del trabajador]")}</strong>
    ${area ? ` en el área de <strong>${escaparHTML(area)}</strong>` : ""}
    , desde el <strong>${formatearFechaLarga(fechaIngreso)}</strong>
    ${
      sigueLaborando
        ? " hasta la fecha, cumpliendo sus funciones de manera regular y de acuerdo con las responsabilidades inherentes a su puesto."
        : ` hasta el <strong>${formatearFechaLarga(fechaCese)}</strong>.`
    }
  </p>
  ${
    remuneracionTexto
      ? `<p class="indent">Asimismo, se deja constancia de que percibe (o percibió) una remuneración mensual de <strong>${remuneracionTexto}</strong>.</p>`
      : ""
  }
  ${
    horarioTexto
      ? `<p class="indent">Su jornada ordinaria de trabajo corresponde a <strong>${horarioTexto}</strong>.</p>`
      : ""
  }
  <p class="indent">
    La presente constancia se expide a solicitud del interesado(a), para los fines que estime
    convenientes, sin que ello implique reconocimiento de derechos distintos a los derivados de la
    relación laboral mantenida entre las partes.
  </p>
  <p class="fecha"><strong>${escaparHTML(ciudad || "____________")}</strong>, ${formatearFechaLarga(fechaEmision)}.</p>
  <div class="firma">
    <div class="linea"></div>
    <p><strong>${escaparHTML(representante || "[Representante Legal]")}</strong></p>
    <p>${escaparHTML(cargoRepresentante || "[Cargo del representante]")}</p>
    <p><strong>${escaparHTML(empresa || "[Razón Social]")}</strong></p>
    ${ruc ? `<p>RUC N.° ${escaparHTML(ruc)}</p>` : ""}
  </div>
</body>
</html>`;

    const ventana = window.open("", "_blank", "width=800,height=1000");
    if (!ventana) {
      alert("Tu navegador bloqueó la ventana emergente. Habilita las ventanas emergentes para este sitio e inténtalo de nuevo.");
      return;
    }
    ventana.document.open();
    ventana.document.write(html);
    ventana.document.close();
    ventana.onload = () => {
      ventana.focus();
      ventana.print();
    };
  }

  return (
    <div className="w-full min-h-full px-4 py-10 bg-stone-50">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center text-lg text-white bg-red-700 rounded-full h-11 w-11">
            📄
          </div>
          <div>
            <p className="text-xl font-bold tracking-[0.25em] uppercase">Constancia de Trabajo</p>
            <p className="mt-1 text-sm text-slate-500">Constancia N.° 001-{hoy.getFullYear()}</p>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="p-6 space-y-6 bg-white border shadow-sm rounded-2xl border-slate-200">
            <div>
              <p className={seccionClase}>Datos del empleador</p>
              <div className="space-y-3">
                <div>
                  <label className={labelClase}>Razón social {requerido}</label>
                  <input value={empresa} onChange={(e) => setEmpresa(e.target.value)} className={inputClase} placeholder="Comercial Andina S.A.C." />
                </div>
                <div>
                  <label className={labelClase}>RUC {requerido}</label>
                  <input value={ruc} onChange={(e) => setRuc(e.target.value)} className={inputClase} placeholder="20601234567" />
                </div>
                <div>
                  <label className={labelClase}>Dirección</label>
                  <input value={direccion} onChange={(e) => setDireccion(e.target.value)} className={inputClase} placeholder="Av. Javier Prado 123, San Isidro" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClase}>Representante legal {requerido}</label>
                    <input value={representante} onChange={(e) => setRepresentante(e.target.value)} className={inputClase} />
                  </div>
                  <div>
                    <label className={labelClase}>Cargo del representante {requerido}</label>
                    <input value={cargoRepresentante} onChange={(e) => setCargoRepresentante(e.target.value)} className={inputClase} placeholder="Gerente de RR.HH." />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <p className={seccionClase}>Datos del trabajador</p>
              <div className="space-y-3">
                <div>
                  <label className={labelClase}>Nombres y apellidos {requerido}</label>
                  <input value={trabajador} onChange={(e) => setTrabajador(e.target.value)} className={inputClase} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClase}>Documento de identidad {requerido}</label>
                    <select value={tipoDocumento} onChange={(e) => setTipoDocumento(e.target.value)} className={inputClase}>
                      <option value="DNI">DNI</option>
                      <option value="CE">CE</option>
                      <option value="Pasaporte">Pasaporte</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClase}>Número de documento {requerido}</label>
                    <input value={numeroDocumento} onChange={(e) => setNumeroDocumento(e.target.value)} className={inputClase} />
                  </div>
                </div>
                <div>
                  <label className={labelClase}>Cargo del trabajador {requerido}</label>
                  <input value={cargo} onChange={(e) => setCargo(e.target.value)} className={inputClase} placeholder="Analista Contable" />
                </div>
                <div>
                  <label className={labelClase}>Área / Departamento</label>
                  <input value={area} onChange={(e) => setArea(e.target.value)} className={inputClase} placeholder="Contabilidad y Finanzas" />
                </div>
              </div>
            </div>

            <div>
              <p className={seccionClase}>Datos laborales</p>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClase}>Fecha de ingreso {requerido}</label>
                    <input type="date" value={fechaIngreso} onChange={(e) => setFechaIngreso(e.target.value)} className={inputClase} />
                  </div>
                  <div>
                    <label className={labelClase}>Fecha de cese (opcional)</label>
                    <input type="date" value={fechaCese} onChange={(e) => setFechaCese(e.target.value)} className={inputClase} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClase}>Fecha de emisión</label>
                    <input type="date" value={fechaEmision} onChange={(e) => setFechaEmision(e.target.value)} className={inputClase} />
                  </div>
                  <div>
                    <label className={labelClase}>Ciudad</label>
                    <input value={ciudad} onChange={(e) => setCiudad(e.target.value)} className={inputClase} />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <p className={seccionClase}>Opciones del documento</p>
              <div className="space-y-4">
                <div>
                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={incluirRemuneracion}
                      onChange={(e) => setIncluirRemuneracion(e.target.checked)}
                      className="w-4 h-4 text-red-700 rounded border-slate-300 focus:ring-red-700/30"
                    />
                    <span className="text-sm text-slate-700">Incluir remuneración mensual</span>
                  </label>
                  <p className="mt-1 ml-6 text-xs text-slate-400">Menciona el monto percibido en la constancia</p>
                  {incluirRemuneracion && (
                    <div className="mt-2 ml-6">
                      <div className="relative max-w-[220px]">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">S/</span>
                        <input
                          type="number"
                          min="0"
                          value={remuneracion}
                          onChange={(e) => setRemuneracion(e.target.value)}
                          className={`${inputClase} pl-9`}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={incluirHorario}
                      onChange={(e) => setIncluirHorario(e.target.checked)}
                      className="w-4 h-4 text-red-700 rounded border-slate-300 focus:ring-red-700/30"
                    />
                    <span className="text-sm text-slate-700">Incluir horario de trabajo</span>
                  </label>
                  <p className="mt-1 ml-6 text-xs text-slate-400">Especifica la jornada laboral habitual</p>
                  {incluirHorario && (
                    <div className="mt-2 ml-6">
                      <input
                        value={horario}
                        onChange={(e) => setHorario(e.target.value)}
                        className={`${inputClase} max-w-[320px]`}
                        placeholder="lunes a viernes de 9:00 a.m. a 6:00 p.m."
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={imprimirConstancia}
              disabled={!documentoIniciado}
              className="w-full py-3 text-sm font-medium text-white transition-colors bg-red-700 rounded-lg hover:bg-red-800 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Imprimir / Guardar como PDF
            </button>
          </div>

          {/* Vista previa */}
          <div className="bg-white border shadow-sm rounded-2xl border-slate-200">
            <div className="p-10 text-sm leading-relaxed text-slate-800">
              {documentoIniciado ? (
                textoConstancia
              ) : (
                <div className="flex flex-col items-center justify-center py-32 text-center">
                  <div className="mb-5 text-7xl">📄</div>
                  <h3 className="text-2xl font-semibold text-slate-800">Vista previa del documento</h3>
                  <p className="max-w-sm mt-3 text-slate-500">
                    Completa los datos del formulario para visualizar automáticamente la constancia
                    de trabajo.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <p className="mt-4 text-xs leading-relaxed text-center text-slate-400">
          Documento generado como modelo referencial. Verifica que cumpla con el formato interno de
          tu empresa antes de firmarlo o emitirlo oficialmente.
        </p>
      </div>
    </div>
  );
}